import { FileTransmissionError, type IFileTransmissionProgressListener } from '@/core/FileTransmissionProgressListener'
import type { HttpRequestCancelToken } from '@/core/HttpRequestCancelToken'
import { TransmissionState } from '@/core/databus/IMessage'
import { IOSEventName, NativeBridge } from '../IOSUtils'
import { WebEventName } from '..'
import { parseJson } from '@/core/decorators/JSONParamsParseDecorator'
import type { AbsDownloadTask } from '@/core/transmission/AbsDownloadTask'
import { TaskState } from '@/core/transmission/ITask'

enum TransmissionOperateState {
  /**
   * 开始传输
   */
  START_TRANSMISSION = 0,
  /**
   * 删除传输
   */
  DELETE_TRANSMISSION = 1,
  /**
   * 重新开始传输
   */
  RESUME_TRANSMISSION = 2,
}

interface BaseNativeDownloadTaskInfo {
  id: string
  url: string
  name: string
  size: number
  fileType: string
  fileFormat: string
  operate?: TransmissionOperateState
}

interface NativeDownloadTaskInfo extends BaseNativeDownloadTaskInfo {
  progressListener: IFileTransmissionProgressListener
  task: AbsDownloadTask
  requestCancelToken?: HttpRequestCancelToken
}

interface NativeDownloadInfo {
  id: string
  url: string
  completionRatio: number
  state: TransmissionState
  reason?: string
}

class NativeDownloadManager {
  #downloadTaskMap: Map<string, NativeDownloadTaskInfo> = new Map()

  constructor() {
    NativeBridge.getInstance().registerNativeMethod(WebEventName.UPDATE_DOWNLOAD_INFO, (arg: unknown) => {
      this.updateInfo(arg)
    })
  }

  download(nativeDownloadTaskInfo: NativeDownloadTaskInfo) {
    this.#downloadTaskMap.set(nativeDownloadTaskInfo.id, nativeDownloadTaskInfo)

    console.log(
      `[Transmission]: native download start  ${new Date().toLocaleString()}`,
      nativeDownloadTaskInfo,
      this.#downloadTaskMap
    )
    this.#operateImpl(TransmissionOperateState.START_TRANSMISSION, nativeDownloadTaskInfo.id)
  }

  deleteTask(id: string) {
    // console.log(`[Transmission]: native download deleteTask ${id}  ${new Date().toLocaleString()}`)
    this.#operateImpl(TransmissionOperateState.DELETE_TRANSMISSION, id)
  }

  resumeTask(id: string) {
    // console.log(`[Transmission]: native download resumeTask ${id}   ${new Date().toLocaleString()}`)
    this.#operateImpl(TransmissionOperateState.RESUME_TRANSMISSION, id)
  }

  @parseJson
  updateInfo(nativeDownloadInfoData: unknown) {
    const nativeDownloadInfo = nativeDownloadInfoData as unknown as NativeDownloadInfo

    try {
      const nativeDownloadTaskInfo = this.#downloadTaskMap.get(nativeDownloadInfo.id)

      if (nativeDownloadTaskInfo) {
        if ([TaskState.SUCCESS, TaskState.FAILED, TaskState.PAUSE].includes(nativeDownloadTaskInfo.task.state)) return

        switch (nativeDownloadInfo.state) {
          case TransmissionState.DONE_FAILED:
            nativeDownloadTaskInfo.progressListener.onError(FileTransmissionError.UNKNOWN)
            console.log(`[Transmission]: native updateInfo  error  ${new Date().toLocaleString()}`, nativeDownloadInfo)
            break
          case TransmissionState.DONE_SUCCEED:
            nativeDownloadTaskInfo.progressListener.onCompleted()

            break
          case TransmissionState.ON_GOING:
            const transmitted = nativeDownloadInfo.completionRatio * nativeDownloadTaskInfo.size

            nativeDownloadTaskInfo.progressListener.onProgressChanged(transmitted, nativeDownloadTaskInfo.size)

            break
          default:
            nativeDownloadTaskInfo.progressListener.onError(FileTransmissionError.UNKNOWN)

            console.log(`[Transmission]: native updateInfo  error  ${new Date().toLocaleString()}`, nativeDownloadInfo)

            break
        }
      } else {
        console.log(
          `[Transmission]: native updateInfo  error  ${new Date().toLocaleString()} do not register  `,
          nativeDownloadInfo
        )
      }
    } catch (error) {
      console.log(`[Transmission]: native updateInfo  error  ${new Date().toLocaleString()}   `, error)
    }
  }

  #operateImpl(operate: TransmissionOperateState, id: string) {
    const baseNativeDownloadTaskInfo = this.#getBaseNativeDownloadTaskInfo(id)

    console.log(
      `[Transmission]: native download operateImpl ${operate}  ${new Date().toLocaleString()}`,
      baseNativeDownloadTaskInfo
    )

    if (baseNativeDownloadTaskInfo) {
      NativeBridge.getInstance().callNativeMethod<BaseNativeDownloadTaskInfo>(IOSEventName.DOWNLOAD_FILE, {
        ...baseNativeDownloadTaskInfo,
        operate,
      })
    } else {
      console.log(
        `[Transmission]: native operate Task  error  ${new Date().toLocaleString()}  id: ${id} operate:${operate} `
      )
    }
  }

  #getBaseNativeDownloadTaskInfo(id: string): BaseNativeDownloadTaskInfo | undefined {
    const targetTask = this.#downloadTaskMap.get(id)

    if (targetTask) {
      const { progressListener, requestCancelToken, task, ...baseNativeDownloadTaskInfo } = targetTask

      return baseNativeDownloadTaskInfo
    }
    console.log(`[Transmission]: native get Task  error  ${new Date().toLocaleString()}   `, id)

    return undefined
  }
}

const INSTANCE = new NativeDownloadManager()

export { INSTANCE }
